// Demo step 3: every product batch with its latest as_is and assume_open evaluation.
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../components/Layout.tsx';
import { Link } from 'react-router';
import { Classification, DemoStamp, ErrorBox, Loading, StatusBadge } from '../components/ui.tsx';
import { latestEvaluations, passports, productBatches, type PassportRow } from '../lib/data.ts';
import { invoke } from '../lib/supabase.ts';
import type { EvaluationRow, NodeRow } from '../lib/types.ts';

export function Batches() {
  const { t } = useTranslation();
  const [batches, setBatches] = useState<NodeRow[] | null>(null);
  const [evals, setEvals] = useState<Map<string, EvaluationRow>>(new Map());
  const [published, setPublished] = useState<Map<string, PassportRow>>(new Map());
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setBatches(await productBatches());
      setEvals(await latestEvaluations());
      setPublished(await passports());
    } catch (e) {
      setError(e);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  async function evaluateAll() {
    setBusy(true);
    setError(null);
    try {
      for (const b of batches ?? []) await invoke('evaluate', { batch_id: b.node_id, mode: 'as_is' });
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!batches) return <Loading />;
  return (
    <main>
      <PageHeader title={t('batches.title')} page="batches">
        <Link className="button" to="/batches/new">
          {t('register.title')}
        </Link>
      </PageHeader>
      <ErrorBox error={error} />
      {batches.length === 0 ? (
        <p>{t('batches.none')}</p>
      ) : (
        <>
          <button disabled={busy} onClick={() => void evaluateAll()}>
            {t('batches.evaluate_all')}
          </button>
          <table>
            <thead>
              <tr>
                <th>{t('col.batch')}</th>
                <th>{t('col.product')}</th>
                <th>as_is</th>
                <th>assume_open</th>
                <th>{t('col.passport')}</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.node_id}>
                  <td className="nowrap">
                    <Link to={`/batches/${b.node_id}`}>{b.node_id}</Link>
                  </td>
                  <td className="nowrap">{b.prod_code}</td>
                  {(['as_is', 'assume_open'] as const).map((m) => {
                    const e = evals.get(`${b.node_id}|${m}`);
                    return (
                      <td key={m}>
                        {e ? (
                          <>
                            <StatusBadge status={e.display_status} /> <Classification verdict={e.verdict} />
                            {e.verdict.assumptions.length > 0 && (
                              <DemoStamp count={e.verdict.assumptions.length} />
                            )}
                          </>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    );
                  })}
                  <td className="nowrap">
                    {published.get(b.node_id) ? (
                      <Link to={`/p/${published.get(b.node_id)?.upi ?? ''}`}>
                        {t('batches.view_passport')}
                      </Link>
                    ) : (
                      <span className="muted">{t('batches.no_passport')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
